import { FormEvent, useEffect, useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight, Heart, MessageCircle, Send, ThumbsUp, X } from 'lucide-react';
import PageHeader from '@/components/PageHeader';
import { supabase } from '../../lib/supabase';

type Comment = { id: string; photo_index: number; author_name: string; content: string; created_at: string };
type Reaction = { id: string; photo_index: number; reaction_type: 'like' | 'love'; visitor_id: string };

const photos = Array.from({ length: 20 }, (_, index) => ({
  src: `/galeria-valentina/foto-${String(index + 1).padStart(2, '0')}.jpg`,
  alt: `Ensaio de quinze anos da Valentina — foto ${index + 1}`,
}));

function visitorId() {
  const key = 'valentina15-gallery-visitor';
  let value = localStorage.getItem(key);
  if (!value) {
    value = crypto.randomUUID();
    localStorage.setItem(key, value);
  }
  return value;
}

export default function Gallery() {
  const [selected, setSelected] = useState<number | null>(null);
  const [comments, setComments] = useState<Comment[]>([]);
  const [reactions, setReactions] = useState<Reaction[]>([]);
  const [author, setAuthor] = useState('');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [feedback, setFeedback] = useState('');
  const me = useMemo(visitorId, []);

  async function loadSocial() {
    const [{ data: commentData }, { data: reactionData }] = await Promise.all([
      supabase.from('valentina_photo_comments').select('*').eq('approved', true).order('created_at', { ascending: true }),
      supabase.from('valentina_photo_reactions').select('*'),
    ]);
    setComments((commentData ?? []) as Comment[]);
    setReactions((reactionData ?? []) as Reaction[]);
  }

  useEffect(() => { void loadSocial(); }, []);
  useEffect(() => {
    document.body.classList.toggle('gallery-modal-open', selected !== null);
    const onKey = (event: KeyboardEvent) => {
      if (selected === null) return;
      if (event.key === 'Escape') setSelected(null);
      if (event.key === 'ArrowLeft') setSelected((selected + photos.length - 1) % photos.length);
      if (event.key === 'ArrowRight') setSelected((selected + 1) % photos.length);
    };
    window.addEventListener('keydown', onKey);
    return () => { document.body.classList.remove('gallery-modal-open'); window.removeEventListener('keydown', onKey); };
  }, [selected]);

  const count = (index: number, type?: string) => reactions.filter((r) => r.photo_index === index && (!type || r.reaction_type === type)).length;
  const commentCount = (index: number) => comments.filter((c) => c.photo_index === index).length;

  async function toggleReaction(type: 'like' | 'love') {
    if (selected === null) return;
    const existing = reactions.find((r) => r.photo_index === selected && r.reaction_type === type && r.visitor_id === me);
    if (existing) await supabase.from('valentina_photo_reactions').delete().eq('id', existing.id);
    else await supabase.from('valentina_photo_reactions').insert({ photo_index: selected, reaction_type: type, visitor_id: me });
    await loadSocial();
  }

  async function submitComment(event: FormEvent) {
    event.preventDefault();
    if (selected === null || !author.trim() || !message.trim()) return;
    setSending(true); setFeedback('');
    const { error } = await supabase.from('valentina_photo_comments').insert({
      photo_index: selected, author_name: author.trim(), content: message.trim(), approved: true,
    });
    setSending(false);
    if (error) { setFeedback('Não foi possível publicar agora. Tente novamente.'); return; }
    setMessage(''); setFeedback('Comentário publicado!'); await loadSocial();
  }

  return <main className="valentina-gallery-page">
    <PageHeader />

    <img className="valentina-gallery-flower valentina-gallery-flower-top" src="/envelope-floral-top-left.png" alt="" />
    <img className="valentina-gallery-flower valentina-gallery-flower-bottom" src="/envelope-floral-bottom-right.png" alt="" />

    <section className="valentina-gallery-hero">
      <p className="eyebrow">MEMÓRIAS PARA SEMPRE</p>
      <h1>Galeria de fotos</h1>
      <p>Um pedacinho deste momento especial para compartilhar, curtir e guardar no coração.</p>
      <div className="ornament"><span/><Heart size={14} fill="currentColor"/><span/></div>
    </section>

    <section className="valentina-gallery-grid" aria-label="Fotos da Valentina">
      {photos.map((photo, index) => <button key={photo.src} className="valentina-photo-card" onClick={() => setSelected(index)}>
        <img src={photo.src} alt={photo.alt}/>
        <span className="valentina-photo-number">{String(index + 1).padStart(2, '0')}</span>
        <span className="valentina-photo-stats"><Heart size={15} fill="currentColor"/> {count(index)} <MessageCircle size={15}/> {commentCount(index)}</span>
      </button>)}
    </section>

    {selected !== null && <div className="valentina-lightbox" role="dialog" aria-modal="true" aria-label={`Foto ${selected + 1}`} onMouseDown={(e) => { if (e.target === e.currentTarget) setSelected(null); }}>
      <button className="lightbox-close" onClick={() => setSelected(null)} aria-label="Fechar"><X/></button>
      <button className="lightbox-nav lightbox-prev" onClick={() => setSelected((selected + photos.length - 1) % photos.length)} aria-label="Foto anterior"><ChevronLeft/></button>
      <div className="valentina-lightbox-content">
        <div className="valentina-lightbox-photo"><img src={photos[selected].src} alt={photos[selected].alt}/><span>{selected + 1} / {photos.length}</span></div>
        <aside className="valentina-social-panel">
          <div className="social-heading"><small>FOTO {String(selected + 1).padStart(2, '0')}</small><h2>Deixe seu carinho</h2></div>
          <div className="reaction-row">
            <button className={reactions.some(r => r.photo_index === selected && r.reaction_type === 'like' && r.visitor_id === me) ? 'active' : ''} onClick={() => void toggleReaction('like')}><ThumbsUp size={17}/> Curtir <b>{count(selected, 'like')}</b></button>
            <button className={reactions.some(r => r.photo_index === selected && r.reaction_type === 'love' && r.visitor_id === me) ? 'active' : ''} onClick={() => void toggleReaction('love')}><Heart size={17}/> Amei <b>{count(selected, 'love')}</b></button>
          </div>
          <div className="photo-comments">
            {comments.filter(c => c.photo_index === selected).length === 0 && <p className="empty-comments">Seja a primeira pessoa a comentar esta foto.</p>}
            {comments.filter(c => c.photo_index === selected).map(c => <article key={c.id}><strong>{c.author_name}</strong><p>{c.content}</p><time>{new Date(c.created_at).toLocaleDateString('pt-BR')}</time></article>)}
          </div>
          <form className="photo-comment-form" onSubmit={submitComment}>
            <input value={author} onChange={e => setAuthor(e.target.value)} maxLength={60} placeholder="Seu nome" required/>
            <textarea value={message} onChange={e => setMessage(e.target.value)} maxLength={280} placeholder="Escreva um comentário..." required/>
            <button disabled={sending}><Send size={15}/>{sending ? 'Enviando...' : 'Comentar'}</button>
            {feedback && <p className="comment-feedback">{feedback}</p>}
          </form>
        </aside>
      </div>
      <button className="lightbox-nav lightbox-next" onClick={() => setSelected((selected + 1) % photos.length)} aria-label="Próxima foto"><ChevronRight/></button>
    </div>}
  </main>;
}
